import { inject, Injectable } from '@angular/core';
import { UserDto } from '@app/shared/models/UserDto';
import { environment } from '@env/environment';
import { __, MbLoadingBarService, MbResponse, MbUser, MbUserService } from '@montblancsimpl/core';
import { ToastrService } from 'ngx-toastr';
import { from, Observable, of, Subject, zip } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AppRoute } from '@app/shared/app.route.enum';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { OktaAuth } from '@okta/okta-auth-js';
import { OKTA_AUTH } from '@okta/okta-angular';

export class Credentials {
  user: MbUser;
  permissions: string[];
  expires: Date;
}

export enum UserRole {
  ApplicationAdmin = 'Application.Admin',

  CustomsUser = 'CustomsUser',

  DeliveryNotificationManager = 'DeliversyNotificationManager',

  DeliveryNotificationUser = 'DeliveryNotificationUser',

  DisUser = 'Application.User',

  DisTester = 'QualityAssuranceTester',

  ReturnManager = 'ReturnManager',

  ReturnUser = 'ReturnUser',
}

const credentialsKey = 'credentials_jsuhisjdfuhsdijf';

/**
 * Provides a base for authentication workflow.
 */
@Injectable()
export class AuthenticationService {
  private oktaAuth = inject(OKTA_AUTH);

  private _credentials: Credentials | null;

  private __currentUser: UserDto | null;

  private _credentials$: Subject<Credentials> = new Subject<Credentials>();

  constructor(
    private mbUserService: MbUserService,
    private toastr: ToastrService,
    protected httpClient: HttpClient,
    private router: Router,
    private loadingBarService: MbLoadingBarService,
  ) {}

  getUser() {
    return this.__currentUser;
  }

  getCurrentUser() {
    return this.httpClient.get<MbResponse<UserDto>>(`users/me`).pipe(
      tap((data: any) => {
        this.__currentUser = data.data;
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          return from(this.oktaAuth.token.getWithoutPrompt()).pipe(
            switchMap(() => {
              return this.httpClient.get<MbResponse<UserDto>>(`users/me`).pipe(
                tap((data: any) => {
                  this.__currentUser = data.data;
                }),
                catchError((err) => {
                  this.handleError(err);
                  return of(err);
                }),
              );
            }),
            catchError((err) => {
              this.handleError(err);
              return of(err);
            }),
          );
        }
        this.handleError(error);
        return of(error);
      }),
    );
  }

  private handleError(error: any) {
    console.log('navigateToForbidden', 'getCurrentUser', error);
    this.router.navigate([`${AppRoute.Forbidden}`]);
    this.loadingBarService.complete();
  }

  hasPermission(permission: string): boolean {
    if (__.IsNullOrUndefinedOrEmpty(permission)) {
      return true;
    }
    if (!this.isAuthenticated()) {
      return false;
    }
    if (environment.testPermissions === false) {
      const isQaTester =
        this.credentials.permissions.findIndex((q) => q === environment.umcQATesterRole) > -1;

      if (isQaTester === true) {
        return true;
      }
    }
    return this.credentials.permissions.indexOf(permission) > -1;
  }

  hasAnyPermission(permissions: string[]): boolean {
    if (__.IsNullOrUndefined(permissions)) {
      return true;
    }
    if (!this.isAuthenticated()) {
      return false;
    }
    if (environment.testPermissions === false) {
      const isQaTester =
        this.credentials.permissions.findIndex((q) => q === environment.umcQATesterRole) > -1;

      if (isQaTester === true) {
        return true;
      }
    }
    return this.credentials.permissions.some((permission: string) => {
      const s = '';
      return permissions.findIndex((a) => permission === a) > -1;
    });
  }

  /**
   * Authenticates the user.
   * @param {MbUser} user The user that should be logged in.
   * @return {Observable<Credentials>} The user credentials.
   */
  login(): Observable<Credentials> {
    return zip(
      this.mbUserService.getLoggedInUser().pipe(
        catchError((error: any) => {
          this.toastr.error(
            'It seems like the server is not accessible. Please make sure that you are connected to the internet.',
          );
          return of(null);
        }),
      ),
      this.mbUserService
        .getPermissionsByLoggedInUserByApplication(environment.umcApplicationKey)
        .pipe(
          catchError((error: any) => {
            this.toastr.error(
              'It seems like the server is not accessible. Please make sure that you are connected to the internet.',
            );
            return of([]);
          }),
        ),
      this.getCurrentUser(),
    ).pipe(
      catchError((error: any) => {
        return of([null, []]);
      }),
      map((userAndPermissionsResult: any) => {
        let credentials = null;

        if (
          __.IsNullOrUndefined(userAndPermissionsResult[0]) ||
          __.IsNullOrUndefined(userAndPermissionsResult[1]) ||
          userAndPermissionsResult[2] instanceof HttpErrorResponse
        ) {
          credentials = <Credentials>{
            isErroneous: false,
            isLoading: false,
            user: null,
            permissions: [],
            expires: new Date(),
          };
        } else {
          if (
            !__.IsNullOrUndefined(
              userAndPermissionsResult[0].data && userAndPermissionsResult[1].data.length !== 0,
            )
          ) {
            credentials = <Credentials>{
              isErroneous: false,
              isLoading: false,
              user: userAndPermissionsResult[0].data,
              permissions: userAndPermissionsResult[1].data,
              expires: new Date(
                new Date().getFullYear(),
                new Date().getMonth(),
                new Date().getDate(),
                new Date().getHours(),
                new Date().getMinutes() + environment.loginExpiryTime,
                new Date().getSeconds(),
              ),
            };
          } else {
            credentials = <Credentials>{
              isErroneous: false,
              isLoading: false,
              user: null,
              permissions: [],
              expires: new Date(),
            };
          }
        }

        this.setCredentials(Object.assign(new Credentials(), credentials));
        return credentials;
      }),
    );
  }

  /**
   * Logs out the user and clear credentials.
   * @return {Observable<boolean>} True if the user was logged out successfully.
   */
  logout(): void {
    // Customize credentials invalidation here
    this.setCredentials();
    this.httpClient
      .get(`${environment.apiUrl}users/me`, {
        headers: new HttpHeaders({ 'x-end-session': 'true' }),
      })
      .subscribe(() => {});
    this.oktaAuth.signOut().then();
  }

  /**
   * Checks is the user is authenticated.
   * @return {boolean} True if the user is authenticated.
   */
  isAuthenticated(): boolean {
    return !__.IsNullOrUndefined(this.credentials);
  }

  /**
   * Checks if the user has any permissions stored in the credentials.
   * @return {boolean} True if the user has stored permissions.
   */
  hasStoredPermissions(): boolean {
    return !__.IsNullOrUndefined(this.credentials) && this.credentials.permissions.length !== 0;
  }

  /**
   * Checks if the authentication is expired.
   * It checks both the local credentials expiration and the Okta token validity.
   * @return {boolean} True if the authentication is expired.
   */
  isAuthenticationExpired(): boolean {
    return this.credentials.expires < new Date() || !this.oktaAuth.getAccessToken();
  }

  /**
   * Gets the user credentials.
   * @return {Credentials} The user credentials or null if the user is not authenticated.
   */
  get credentials(): Credentials | null {
    return this._credentials;
  }

  /**
   * Sets the user credentials.
   * The credentials may be persisted across sessions by setting the `remember` parameter to true.
   * Otherwise, the credentials are only persisted for the current session.
   * @param {Credentials=} credentials The user credentials.
   * @param {boolean=} remember True to remember credentials across sessions.
   */
  private setCredentials(credentials?: Credentials) {
    this._credentials = credentials || null;
    this._credentials$.next(credentials);

    if (credentials) {
      const storage: Storage = sessionStorage;
      storage.setItem(credentialsKey, JSON.stringify(credentials));
    } else {
      sessionStorage.removeItem(credentialsKey);
      localStorage.removeItem(credentialsKey);
    }
  }

  getRolesOfCurrentLoggedInUser(): string[] {
    const credentials = Object.assign(
      new Credentials(),
      JSON.parse(sessionStorage.getItem(credentialsKey)),
    );
    if (!__.IsNullOrUndefined(credentials) && credentials.permissions.length !== 0) {
      return credentials.permissions;
    }
    return [];
  }
}
